<template>
  <el-dialog
    :model-value="visible"
    :before-close="() => {
      emit('close');
    }"
    width="500"
  >
    <template #header>
      <div class="my-header">
        <h4 class="px-2 pb-3 text-xl text-[#344767] font-semibold">
          {{ type === 'add' ? '新增' : '编辑' }}
        </h4>
      </div>
    </template>
    <div class="px-2 pb-3">
      <el-form
        ref="formRef"
        class="add-platform-users-form"
        require-asterisk-position="right"
        label-position="top"
        :model="formValues"
        :rules="formRules"
      >
        <el-form-item v-if="!isCompany" prop="companyName" label="来账企业">
          <el-input v-model="formValues.companyName" maxlength="30" placeholder="请输入" clearable @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item prop="bankName" label="来账银行名称">
          <el-input v-model="formValues.bankName" maxlength="30" placeholder="请输入" clearable @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item prop="bankAccount" label="来账银行账号">
          <el-input v-model="formValues.bankAccount" maxlength="30" placeholder="请输入" clearable @keyup.enter="onConfirm" />
        </el-form-item>
        <el-form-item v-if="!isCompany" prop="unitNo" label="关联企业名称">
          <el-select
            v-model="formValues.unitNo"
            placeholder="请选择"
            clearable
            filterable
            @keyup.enter="onConfirm"
          >
            <el-option
              v-for="item in companyList"
              :key="item.id"
              :label="item.companyName"
              :value="item.unitNo"
            />
          </el-select>
        </el-form-item>
        <el-form-item v-if="!isCompany" label="关联企业编号">
          <el-input v-model="companyNo" disabled />
        </el-form-item>
        <el-form-item v-if="isCompany" label="备注">
          <el-input v-model="formValues.remark" placeholder="请输入" class="ipt" type="textarea" :autosize="{ minRows: 4, maxRows: 8 }" />
        </el-form-item>
      </el-form>
    </div>

    <template #footer>
      <div class="flex justify-center">
        <el-button @click="emit('close')">
          取消
        </el-button>
        <el-button type="primary" :loading="loading" @click="onConfirm">
          确定
        </el-button>
      </div>
    </template>
  </el-dialog>
</template>

<script setup lang="ts">
import { ElMessage, FormInstance, FormRules } from 'element-plus';
import { computed, nextTick, ref, watch } from 'vue';
import { addUnitBankAccount, queryAlipayUnitList, useUserStore, reapplyUnitBankAccount } from '@core/main';
import { ESystemType } from '@core/api';

const emit = defineEmits(['close', 'confirm']);

const props = defineProps({
  visible: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'add',
  },
  row: {
    type: Object,
    default: () => ({}),
  },
  serverId: {
    type: String,
    default: '',
  },
});

const { state: userState } = useUserStore();

const isCompany = computed(() => userState.identity.systemType === ESystemType.Company);

const formRef = ref<FormInstance>();
const formValues = ref({} as any);

const loading = ref(false);

const companyList = ref<Array<any>>([]);
const companyNo = computed(() => {
  return companyList.value.find((item) => item.unitNo === formValues.value.unitNo)?.companyNo;
});

const formRules = ref<FormRules<any>>({
  companyName: [
    {
      required: true,
      trigger: 'blur',
      message: '请输入来账企业',
    },
  ],
  bankName: [
    {
      required: true,
      trigger: 'blur',
      message: '请输入来账银行名称',
    },
  ],
  bankAccount: [
    {
      required: true,
      trigger: 'blur',
      message: '请输入来账银行账号',
    },
  ],
  unitNo: [
    {
      required: true,
      trigger: 'change',
      message: '请选择关联企业',
    },
  ],
});

function onConfirm() {
  formRef.value?.validate(async (valid) => {
    if (valid) {
      doSubmit();
    }
  });
}

async function doSubmit() {
  loading.value = true;
  try {
    if (props.type === 'add') {
      await addUnitBankAccount({
        ...formValues.value,
        serverId: props.serverId,
      });
      ElMessage.success('新增成功');
    } else {
      await reapplyUnitBankAccount({
        ...formValues.value,
        id: props.row.id,
        serverId: props.serverId,
      });
      ElMessage.success('重新申请成功');
    }
    loading.value = false;
    emit('confirm');
  } catch (error) {
    loading.value = false;
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

async function fetchCompanyList() {
  try {
    const res = await queryAlipayUnitList({ serverId: props.serverId });

    companyList.value = res.data;
  } catch (error) {
    if ((error as Error).message) ElMessage.error((error as Error).message);
  }
}

watch(
  [
    () => props.type,
    () => props.visible,
  ],
  ([type, visible]) => {
    if (!visible) {
      formValues.value = {};
      nextTick(() => {
        formRef.value?.resetFields();
      });
    } else {
      fetchCompanyList();
      if (type === 'edit') {
        formValues.value = props.row;
      } else {
        formValues.value = {};
      }
    }
  },
);

</script>
<style lang="scss" scoped>
:deep(.el-select__wrapper) {
  min-height: 40px;
}

:deep(.el-input__wrapper) {
  min-height: 40px;
}
</style>